// Pure view helpers for the Certificate Analysis page, kept out of the .svelte
// component so they can be unit-tested without rendering.
import type { CategoryStats, CriteriaAnalytics, CriterionStats } from '../../api/analytics'

// Fixed display order for the first-class requirement categories.
export const CANONICAL_CATEGORIES = [
  'technology',
  'company_certificate',
  'expert_certificate',
  'business_requirement',
  'experience_requirement',
  'staffing_requirement',
  'service_requirement',
  'domain_requirement',
] as const

export interface Section {
  key: string
  stats: CategoryStats
}

/**
 * Order the category map for display: canonical categories first, then any
 * extra categories the backend surfaced so nothing is hidden.
 */
export function orderedSections(data: CriteriaAnalytics): Section[] {
  const entries = data.categories
  const ordered: Section[] = []
  for (const key of CANONICAL_CATEGORIES) {
    if (entries[key]) ordered.push({ key, stats: entries[key] })
  }
  for (const [key, stats] of Object.entries(entries)) {
    if (!(CANONICAL_CATEGORIES as readonly string[]).includes(key)) {
      ordered.push({ key, stats })
    }
  }
  return ordered
}

/** Satisfied / total summed across every category. */
export function overallTotals(data: CriteriaAnalytics): { satisfied: number; total: number } {
  let satisfied = 0
  let total = 0
  for (const stats of Object.values(data.categories)) {
    satisfied += stats.satisfied
    total += stats.total
  }
  return { satisfied, total }
}

export function percent(satisfied: number, total: number): number {
  return total > 0 ? Math.round((satisfied / total) * 100) : 0
}

export interface StatusSplit {
  met: CriterionStats[]
  unmet: CriterionStats[]
}

/**
 * Split a category's criteria into the ones OG-CS meets and the ones it doesn't,
 * each ordered most-frequently-required first (ties broken alphabetically).
 *
 * Satisfaction is deterministic per criterion: it's judged against the company
 * profile, so a given criterion is either satisfied every time it's required
 * (`satisfied === required`) or never (`satisfied === 0`); it can't be "3 of 4".
 * A criterion counts as met only when fully satisfied; anything short (including
 * the theoretical partial) is treated as unmet.
 */
export function splitByStatus(stats: CategoryStats): StatusSplit {
  const met: CriterionStats[] = []
  const unmet: CriterionStats[] = []
  for (const c of stats.criteria) {
    if (c.required > 0 && c.satisfied >= c.required) met.push(c)
    else unmet.push(c)
  }
  const byFrequency = (a: CriterionStats, b: CriterionStats) =>
    b.required - a.required || a.criterion.localeCompare(b.criterion)
  met.sort(byFrequency)
  unmet.sort(byFrequency)
  return { met, unmet }
}
